import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { api, fmtAUD } from "@/lib/api";
import { Trash2, Minus, Plus, ArrowUpRight, Shield, Key, Package } from "lucide-react";
import { toast } from "sonner";

const CRYPTO_LABELS = { btc: "Bitcoin", ltc: "Litecoin", xmr: "Monero" };

// PHANTOM PHONE — Admin PGP identity.
// Verifiable Ed25519 signing key. The armored block below is a placeholder;
// paste the output of `gpg --armor --export admin@phantomphone.info` in its place.
const PGP_IDENTITY = {
  label: "PHANTOM // ADMIN",
  uid: "Phantom Phone <admin@phantomphone.info>",
  email: "admin@phantomphone.info",
  algo: "ed25519 · cv25519 subkey",
  created: "2026-07-21",
  expires: "2029-07-21",
  fingerprint: "0977 1DBB FE29 1603 4550  50B4 344D 908B FEC1 BCC2",
  key: `-----BEGIN PGP PUBLIC KEY BLOCK-----

mDMEal9AYhYJKwYBBAHaRw8BAQdAJXL1nPzCUIQfd4Xu+DCegwMKoDSupq/w3bpn
+7qnJL+0J1BoYW50b20gUGhvbmUgPGFkbWluQHBoYW50b21waG9uZS5pbmZvPoi1
BBMWCgBdFiEECXcdu/4pFgNFUFC0NE2Qi/7BvMIFAmpfQGIbFIAAAAAABAAObWFu
dTIsMi41KzEuMTIsMiwxAhsDBQkFpIVGBQsJCAcCAiICBhUKCQgLAgQWAgMBAh4H
AheAAAoJEDRNkIv+wbzCpR4BAMwLWrwyrh1lKQj5OS5YY4FFzHqA4LU2Mn2u62+R
xK+zAQCcZxmyJLxrJXoJ+2lg1htDTN8otcn7zxnR/Nj5onTZCbg4BGpfQGISCisG
AQQBl1UBBQEBB0AUycXVPC2fdRzdnr+UGQWdkWG3gHLtEMBEaWCTsf6tYQMBCAeI
mgQYFgoAQhYhBAl3Hbv+KRYDRVBQtDRNkIv+wbzCBQJqX0BiGxSAAAAAAAQADm1h
bnUyLDIuNSsxLjEyLDIsMQIbDAUJBaSFRgAKCRA0TZCL/sG8wiGAAQD9XuqDpDsG
yP2s2TyEb4NefSWHeN23YMhuBeq/jKsa0AEArYoAk5vHAIodkpYjjYqmPpRIBS5i
LJmCAI75OjMxtQg=
=MyYN
-----END PGP PUBLIC KEY BLOCK-----`,
};

export default function Cart() {
  const { items, remove, setQty, clear, total, count } = useCart();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [method, setMethod] = useState("card"); // card | crypto
  const [payCurrency, setPayCurrency] = useState("xmr");
  const [busy, setBusy] = useState(false);
  const [cryptoOrder, setCryptoOrder] = useState(null);
  const [cryptoConfig, setCryptoConfig] = useState({ enabled: false, currencies: [] });
  const [showPgp, setShowPgp] = useState(false);

  useEffect(() => {
    api.get("/config/crypto").then((r) => setCryptoConfig(r.data)).catch(() => {});
  }, []);

  const pollCrypto = (orderId) => {
    const tick = async () => {
      try {
        const r = await api.get(`/payments/crypto/status/${orderId}`);
        setCryptoOrder((prev) => (prev ? { ...prev, payment_status: r.data.payment_status } : prev));
        if (r.data.payment_status === "finished") {
          clear();
          toast.success("Crypto payment confirmed. Your vault is being prepared.");
          return;
        }
      } catch {}
      setTimeout(tick, 8000);
    };
    tick();
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (!email) return toast.error("Enter your email to receive the order confirmation.");
    setBusy(true);
    try {
      if (method === "card") {
        const r = await api.post("/payments/checkout", {
          items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
          origin_url: window.location.origin,
          customer_email: email, customer_name: name,
        });
        window.location.href = r.data.checkout_url;
      } else {
        const r = await api.post("/payments/crypto/checkout", {
          items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
          origin_url: window.location.origin,
          customer_email: email, customer_name: name,
          pay_currency: payCurrency,
        });
        setCryptoOrder({ ...r.data, payment_status: "waiting" });
        pollCrypto(r.data.order_id);
        toast.success("Invoice opened — send the exact amount to the address shown.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Try again.");
    }
    setBusy(false);
  };

  // Crypto pay-await state
  if (cryptoOrder) {
    return (
      <div className="pt-32 pb-24 px-6 md:px-10 min-h-[80svh]" data-testid="crypto-invoice">
        <div className="overline mb-6">Ξ — CRYPTO INVOICE</div>
        <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.9]">Send exactly.</h1>
        <p className="mt-4 text-[#888] max-w-lg">Transmit the exact amount below to the address. Do not send from a smart-contract wallet. This page will refresh automatically when the network confirms.</p>

        <div className="mt-12 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7 border border-[#1A1A1A] p-6 md:p-8">
            <div className="overline">Amount · {CRYPTO_LABELS[cryptoOrder.pay_currency]}</div>
            <div className="font-display text-4xl md:text-5xl mt-3 font-mono break-all" data-testid="crypto-amount">
              {cryptoOrder.pay_amount} <span className="text-[#666] text-2xl">{cryptoOrder.pay_currency.toUpperCase()}</span>
            </div>
            <div className="overline text-[#666] mt-2">≈ {fmtAUD(cryptoOrder.amount_aud)}</div>

            <div className="mt-8 overline">To address</div>
            <div className="mt-2 font-mono text-sm md:text-base break-all bg-[#080808] border border-[#1A1A1A] p-4 text-[#EAEAEA]" data-testid="crypto-address">
              {cryptoOrder.pay_address}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(cryptoOrder.pay_address); toast.success("Address copied"); }}
              className="btn-ghost mt-3"
              data-testid="copy-crypto-address"
            >Copy address →</button>

            {cryptoOrder.pay_extra_id && (
              <div className="mt-8 border border-[#7C3AED] p-4">
                <div className="overline text-[#7C3AED]">Payment ID (required for Monero)</div>
                <div className="font-mono text-sm mt-2 break-all">{cryptoOrder.pay_extra_id}</div>
                <div className="text-[#888] text-xs mt-2">Without this payment ID your funds cannot be credited to your order.</div>
              </div>
            )}

            <div className="mt-8 border-t border-[#1A1A1A] pt-6">
              <div className="overline">Status</div>
              <div className="mt-2 font-display text-2xl">
                <span className={cryptoOrder.payment_status === "finished" ? "text-[#EAEAEA]" : "text-[#7C3AED] blink"}>
                  ● {cryptoOrder.payment_status}
                </span>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="border border-[#1A1A1A] p-6 md:p-8">
              <div className="overline mb-4">Order reference</div>
              <div className="font-mono text-xs text-[#888] break-all">{cryptoOrder.order_id}</div>
              <div className="overline mt-6 mb-3">Devices</div>
              {items.map((i) => (
                <div key={i.lookup_key} className="flex justify-between py-2 border-b border-[#1A1A1A] last:border-0 text-sm">
                  <div className="text-[#EAEAEA]">{i.name} x{i.quantity}</div>
                  <div className="font-mono">{fmtAUD(i.price_aud * i.quantity)}</div>
                </div>
              ))}
              <div className="mt-6 text-[#888] text-xs uppercase tracking-widest">Save this page or the order reference — you'll receive a confirmation email once the network confirms.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-6 md:px-10 min-h-[70svh]" data-testid="cart-page">
      <div className="overline mb-6">Ξ 007 — Cart</div>
      <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.9]">Your Vault</h1>

      {items.length === 0 ? (
        <div className="mt-16 border border-[#1A1A1A] p-12 text-center">
          <div className="text-[#888] mb-8">The vault is empty. Nothing to protect yet.</div>
          <Link to="/shop" className="btn-vault">Browse devices <ArrowUpRight size={14} /></Link>
        </div>
      ) : (
        <div className="mt-16 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <div className="border-t border-[#1A1A1A]">
              {items.map((it) => (
                <div key={it.lookup_key} className="grid grid-cols-12 gap-4 md:gap-6 py-6 border-b border-[#1A1A1A] items-center" data-testid={`cart-item-${it.slug}`}>
                  <div className="col-span-3 md:col-span-2 aspect-square bg-[#080808] relative overflow-hidden">
                    <img src={it.image_url} alt={it.name} className="absolute inset-0 w-full h-full object-cover img-clip" />
                  </div>
                  <div className="col-span-9 md:col-span-5">
                    <div className="flex items-center gap-2 mb-1"><span className={`tier-dot ${it.tier}`} /><span className="overline">{it.tier}</span></div>
                    <div className="font-display text-xl">{it.name.split("//")[0]}</div>
                    <div className="text-[#888] text-xs mt-1">
                      {it.storage ? <><span className="text-[#EAEAEA]">{it.storage}</span> · </> : null}
                      {fmtAUD(it.price_aud)} each
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-3 flex items-center gap-2">
                    <button onClick={() => setQty(it.lookup_key, it.quantity - 1)} className="border border-[#1A1A1A] hover:border-[#333] p-2" data-testid={`qty-minus-${it.slug}`}><Minus size={14} /></button>
                    <div className="w-10 text-center font-mono">{it.quantity}</div>
                    <button onClick={() => setQty(it.lookup_key, it.quantity + 1)} className="border border-[#1A1A1A] hover:border-[#333] p-2" data-testid={`qty-plus-${it.slug}`}><Plus size={14} /></button>
                  </div>
                  <div className="col-span-6 md:col-span-2 flex items-center justify-between md:justify-end gap-4">
                    <div className="font-mono text-[#EAEAEA]">{fmtAUD(it.price_aud * it.quantity)}</div>
                    <button onClick={() => remove(it.lookup_key)} className="text-[#666] hover:text-[#7C3AED] transition-colors" data-testid={`remove-${it.slug}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Tamper-evident shipping */}
            <div className="mt-10 border border-[#1A1A1A] p-6" data-testid="tamper-info">
              <div className="flex items-start gap-4">
                <Package size={22} className="mt-1 text-[#EAEAEA]" />
                <div>
                  <div className="overline mb-2">Tamper-evident shipping</div>
                  <ul className="text-[#888] text-sm space-y-2 leading-relaxed">
                    <li>· Every device screw is coated with glitter nail polish; a macro photograph of the pattern is emailed to you before dispatch. Any mismatch on arrival = do not open.</li>
                    <li>· Sealed in a Faraday bag inside a tamper-evident holographic pouch with a serialised seal number.</li>
                    <li>· Shipped by tracked Australia Post Signature-on-Delivery in metropolitan AU; bonded courier for interstate and international.</li>
                    <li>· Hand delivery in Sydney and Melbourne available at checkout for Phantom-tier orders.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* PGP block */}
            <div className="mt-6 border border-[#1A1A1A] p-6" data-testid="pgp-block">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <Key size={22} className="mt-1 text-[#EAEAEA]" />
                  <div>
                    <div className="overline mb-2">PGP · Encrypted correspondence</div>
                    <p className="text-[#888] text-sm">
                      Encrypt sensitive shipping details to our admin key before submitting them. Encrypt to the fingerprint below and paste the armored ciphertext in the "Name" field — we'll decrypt it in-house.
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowPgp((s) => !s)} className="btn-ghost shrink-0" data-testid="toggle-pgp">
                  {showPgp ? "Hide key ↑" : "Show key ↓"}
                </button>
              </div>
              <div className="mt-4 border border-[#1A1A1A] p-5" data-testid="pgp-identity-admin">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div>
                    <div className="overline text-[#7C3AED]">{PGP_IDENTITY.label}</div>
                    <div className="mt-1 text-[#EAEAEA] text-sm font-mono break-all" data-testid="pgp-uid">{PGP_IDENTITY.uid}</div>
                  </div>
                  <div className="overline text-[#666]">{PGP_IDENTITY.algo} · {PGP_IDENTITY.created} → {PGP_IDENTITY.expires}</div>
                </div>
                <div className="mt-4 font-mono text-[10px] md:text-xs text-[#EAEAEA] break-all">
                  FINGERPRINT · <span data-testid="pgp-fingerprint">{PGP_IDENTITY.fingerprint}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => { navigator.clipboard.writeText(PGP_IDENTITY.fingerprint); toast.success("Fingerprint copied"); }}
                    className="btn-ghost text-[10px]"
                    data-testid="copy-pgp-fingerprint"
                  >Copy fingerprint →</button>
                  <a
                    href={`mailto:${PGP_IDENTITY.email}?subject=Request%20PGP%20public%20key`}
                    className="btn-ghost text-[10px]"
                    data-testid="request-pgp-key"
                  >Request armored key →</a>
                </div>
                {showPgp && (
                  <pre className="mt-4 bg-[#050505] border border-[#1A1A1A] p-3 text-[10px] md:text-xs text-[#EAEAEA] overflow-x-auto whitespace-pre-wrap font-mono" data-testid="pgp-key">{PGP_IDENTITY.key}</pre>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4">
            <div className="border border-[#1A1A1A] p-6 md:p-8 sticky top-32">
              <div className="overline mb-6">Order summary</div>
              <div className="flex justify-between text-sm text-[#888] mb-3">
                <span>Devices</span><span className="text-[#EAEAEA]">{count}</span>
              </div>
              <div className="flex justify-between text-sm text-[#888] mb-3">
                <span>Subtotal</span><span className="text-[#EAEAEA]">{fmtAUD(total)}</span>
              </div>
              <div className="flex justify-between text-sm text-[#888] mb-3">
                <span>Shipping</span><span className="text-[#EAEAEA]">{method === "card" ? "Calculated at checkout" : "Included"}</span>
              </div>
              <div className="border-t border-[#1A1A1A] mt-6 pt-6 flex justify-between items-baseline">
                <div className="overline">Total</div>
                <div className="font-display text-3xl">{fmtAUD(total)}</div>
              </div>

              <div className="mt-8 space-y-3">
                <div>
                  <label className="overline block mb-2">Name</label>
                  <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (or PGP block)" data-testid="checkout-name" />
                </div>
                <div>
                  <label className="overline block mb-2">Email</label>
                  <input className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@domain.tld" data-testid="checkout-email" />
                </div>
              </div>

              {/* Payment method toggle */}
              <div className="mt-6">
                <div className="overline mb-2">Payment method</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMethod("card")}
                    className={`border px-3 py-3 text-xs uppercase tracking-widest ${method === "card" ? "border-[#EAEAEA] text-[#EAEAEA] bg-[#0A0A0A]" : "border-[#1A1A1A] text-[#888]"}`}
                    data-testid="method-card"
                  >Card · Stripe</button>
                  <button
                    disabled={!cryptoConfig.enabled}
                    onClick={() => setMethod("crypto")}
                    className={`border px-3 py-3 text-xs uppercase tracking-widest ${method === "crypto" ? "border-[#7C3AED] text-[#7C3AED] bg-[#0A0A0A]" : "border-[#1A1A1A] text-[#888]"} ${!cryptoConfig.enabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid="method-crypto"
                    title={cryptoConfig.enabled ? "Pay with BTC, LTC, or Monero" : "Crypto not configured yet"}
                  >Crypto · BTC/LTC/XMR</button>
                </div>
                {!cryptoConfig.enabled && method === "card" && (
                  <div className="text-[10px] text-[#666] mt-2 uppercase tracking-widest">Crypto activates once the operator adds NOWPayments credentials.</div>
                )}
              </div>

              {method === "crypto" && cryptoConfig.enabled && (
                <div className="mt-4">
                  <div className="overline mb-2">Currency</div>
                  <div className="grid grid-cols-3 gap-2">
                    {["btc", "ltc", "xmr"].map((c) => (
                      <button
                        key={c}
                        onClick={() => setPayCurrency(c)}
                        className={`border px-3 py-2 text-xs uppercase tracking-widest ${payCurrency === c ? "border-[#EAEAEA] text-[#EAEAEA]" : "border-[#1A1A1A] text-[#888]"}`}
                        data-testid={`currency-${c}`}
                      >{c}</button>
                    ))}
                  </div>
                  <div className="flex items-start gap-2 text-[10px] text-[#666] mt-3 uppercase tracking-widest">
                    <Shield size={12} className="mt-0.5" /> Monero recommended for full privacy.
                  </div>
                </div>
              )}

              <button onClick={checkout} disabled={busy} className={`btn-vault w-full justify-center mt-8 ${method === "crypto" ? "danger" : ""}`} data-testid="checkout-btn">
                {busy ? "Opening secure channel…" : method === "card" ? "Secure checkout" : `Pay with ${payCurrency.toUpperCase()}`}
              </button>
              <div className="mt-4 text-[10px] tracking-widest uppercase text-[#444] text-center">
                Encrypted · {method === "card" ? "Stripe · AUD" : "NOWPayments · Zero-knowledge"}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
